import { createSlice } from "@reduxjs/toolkit";
import { savedLoginPath } from "../../utils/savedRoutes";

const saveAuthToSession = (state) => {
  try {
    const authState = {
      token:               state.token,
      user:                state.user,
      userId:              state.userId,
      isAuthenticated:     state.isAuthenticated,
      role:                state.role,
      roleId:              state.roleId,
      permissions:         state.permissions,
      modules:             state.modules,
      group:               state.group,
      subscription:        state.subscription,
      isTrial:             state.isTrial,
      sellviaNextRefreshAt:state.sellviaNextRefreshAt,
      // ✅ NEW: company fields
      companyId:           state.companyId,
      allowedStoreIds:     state.allowedStoreIds,
      selectedCompanyIds:  state.selectedCompanyIds,
      twoFactorEnabled:    state.twoFactorEnabled,
      side:                state.side,
    };
    localStorage.setItem('authState', JSON.stringify(authState));
  } catch (error) {
    console.error('Error saving auth state to localStorage:', error);
  }
};

const loadAuthFromSession = () => {
  try {
    const savedState = localStorage.getItem('authState');
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (error) {
    console.error('Error loading auth state from localStorage:', error);
  }
  return null;
};

const savedAuth = loadAuthFromSession();

const initialState = {
  user:                savedAuth?.user              || null,
  token:               savedAuth?.token             || null,
  userId:              savedAuth?.userId            || null,
  isAuthenticated:     savedAuth?.isAuthenticated   || false,
  trialStartDate:      null,
  remainingTrialDays:  null,
  isSubscribed:        false,
  subscriptionEndDate: null,
  profilePicture:      null,
  tokenExpiryTimer:    null,
  role:                savedAuth?.role              || null,
  roleId:              savedAuth?.roleId            || null,
  permissions:         savedAuth?.permissions       || [],
  fullPermissions:     [],
  modules:             savedAuth?.modules           || [],
  group:               savedAuth?.group             || null,
  subscription:        savedAuth?.subscription      || null,
  isTrial:             savedAuth?.isTrial           || false,
  roleDetails:         null,
  navigationState: {
    showMasterSettings: false,
    currentRoute:       null,
  },
  sellviaNextRefreshAt: (() => {
    const stored = savedAuth?.sellviaNextRefreshAt;
    if (stored && stored > Date.now()) return stored;
    return Date.now() + 30 * 60 * 1000;
  })(),

  // ✅ NEW: Company-based store access
  // companyId        — the company this user belongs to (null for super-admin)
  // allowedStoreIds  — store_ids from JWT (only this user's company stores)
  // selectedCompanyIds — super-admin UI selection: which companies to view
  companyId:          savedAuth?.companyId         || null,
  allowedStoreIds:    savedAuth?.allowedStoreIds   || [],
  selectedCompanyIds: savedAuth?.selectedCompanyIds|| [], // super-admin only

  // ✅ NEW: Two-Step Verification (Google Authenticator) — whether THIS
  // account currently has it on. Drives main-nav.jsx's hourly session
  // renewal (verify-to-renew instead of the existing silent renewal).
  twoFactorEnabled:   savedAuth?.twoFactorEnabled  || false,

  // "admin" | "customer" — which side this account is on (sent by the login
  // response). Decides which login / area it may use; see utils/accountSide.js.
  side:               savedAuth?.side              || null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login: (state, action) => {
      const payload = action.payload;
      state.user               = payload.user              || null;
      state.token              = payload.token             || null;
      state.userId             = payload.userId            || payload.user?._id || null;
      state.isAuthenticated    = true;
      state.trialStartDate     = payload.trialStartDate    || null;
      state.remainingTrialDays = payload.remainingTrialDays|| null;
      state.profilePicture     = payload.user?.profilePicture || null;
      state.role               = payload.role              || payload.user?.role || null;
      state.roleId             = payload.roleId            || null;
      state.roleDetails        = payload.user?.roleDetails || {
        name: payload.role || payload.user?.role,
        _id:  payload.roleId,
      };
      state.permissions        = payload.user?.permissions || [];
      state.fullPermissions    = payload.fullPermissions   || [];
      state.modules            = payload.modules           || [];
      state.group              = payload.group             || null;
      state.subscription       = payload.subscription      || null;
      state.isTrial            = payload.isTrial           || false;
      state.navigationState.showMasterSettings = false;
      state.sellviaNextRefreshAt = Date.now() + 30 * 60 * 1000;

      // ✅ NEW: Company fields from login response
      state.companyId         = payload.companyId         || null;
      state.allowedStoreIds   = payload.allowedStoreIds   || [];
      state.selectedCompanyIds= []; // reset on new login
      state.twoFactorEnabled  = payload.twoFactorEnabled  || false;
      state.side              = payload.side              || null;

      saveAuthToSession(state);
      if (state.tokenExpiryTimer) { clearTimeout(state.tokenExpiryTimer); state.tokenExpiryTimer = null; }
    },

    setCredentials: (state, action) => {
      const { user, token, role, permissions, modules, isAuthenticated } = action.payload;
      state.user           = user           || state.user;
      state.token          = token          || state.token;
      state.role           = role           || state.role;
      state.permissions    = permissions    || state.permissions;
      state.modules        = modules        || state.modules;
      state.isAuthenticated= isAuthenticated !== undefined ? isAuthenticated : true;
      saveAuthToSession(state);
    },

    logout: (state) => {
      localStorage.removeItem('authState');
      // Explicitly reset all auth fields — DO NOT use Object.keys(initialState) because
      // initialState was captured at module-load time when the user was logged in,
      // so it holds isAuthenticated=true which would prevent logout from working.
      state.user                = null;
      state.token               = null;
      state.userId              = null;
      state.isAuthenticated     = false;
      state.trialStartDate      = null;
      state.remainingTrialDays  = null;
      state.isSubscribed        = false;
      state.subscriptionEndDate = null;
      state.profilePicture      = null;
      state.role                = null;
      state.roleId              = null;
      state.permissions         = [];
      state.fullPermissions     = [];
      state.modules             = [];
      state.group               = null;
      state.subscription        = null;
      state.isTrial             = false;
      state.roleDetails         = null;
      state.navigationState     = { showMasterSettings: false, currentRoute: null };
      state.sellviaNextRefreshAt = Date.now() + 30 * 60 * 1000;
      state.companyId           = null;
      state.allowedStoreIds     = [];
      state.selectedCompanyIds  = [];
      state.twoFactorEnabled    = false;
      state.side                = null;
      if (state.tokenExpiryTimer) { clearTimeout(state.tokenExpiryTimer); state.tokenExpiryTimer = null; }
    },

    setUser: (state, action) => {
      const payload = action.payload;
      if (payload.user)              { state.user              = { ...state.user, ...payload.user }; }
      if (payload.token          !== undefined) state.token              = payload.token;
      if (payload.userId         !== undefined) state.userId             = payload.userId;
      if (payload.trialStartDate !== undefined) state.trialStartDate     = payload.trialStartDate;
      if (payload.remainingTrialDays !== undefined) state.remainingTrialDays = payload.remainingTrialDays;
      if (payload.isAuthenticated !== undefined) state.isAuthenticated   = payload.isAuthenticated;
      if (payload.role           !== undefined) state.role               = payload.role;
      if (payload.roleId         !== undefined) state.roleId             = payload.roleId;
      if (payload.permissions    !== undefined) state.permissions        = payload.permissions;
      if (payload.fullPermissions !== undefined) state.fullPermissions   = payload.fullPermissions;
      if (payload.modules        !== undefined) state.modules            = payload.modules;
      if (payload.group          !== undefined) state.group              = payload.group;
      if (payload.subscription   !== undefined) state.subscription       = payload.subscription;
      if (payload.isTrial        !== undefined) state.isTrial            = payload.isTrial;
      if (payload.role)          { state.roleDetails = { name: payload.role, _id: payload.roleId }; }
      // ✅ NEW: company fields
      if (payload.companyId      !== undefined) state.companyId          = payload.companyId;
      if (payload.allowedStoreIds !== undefined) state.allowedStoreIds   = payload.allowedStoreIds;
      saveAuthToSession(state);
    },

    updateTrialINFO: (state, action) => {
      state.remainingTrialDays = action.payload.remainingTrialDays;
      state.isSubscribed       = action.payload.isSubscribed;
      state.subscriptionEndDate= action.payload.subscriptionEndDate;
      saveAuthToSession(state);
    },

    setTokenExpiryTimer: (state, action) => {
      state.tokenExpiryTimer = action.payload.timer;
    },

    updateNavigationState: (state, action) => {
      state.navigationState = { ...state.navigationState, ...action.payload };
    },

    updateAuthData: (state, action) => {
      if (action.payload.permissions)    state.permissions    = action.payload.permissions;
      if (action.payload.modules)        state.modules        = action.payload.modules;
      if (action.payload.fullPermissions)state.fullPermissions= action.payload.fullPermissions;
      if (action.payload.group)          state.group          = action.payload.group;
      saveAuthToSession(state);
    },

    restoreAuth: (state) => {
      const savedAuth = loadAuthFromSession();
      if (savedAuth) {
        state.token              = savedAuth.token           || null;
        state.user               = savedAuth.user            || null;
        state.userId             = savedAuth.userId          || null;
        state.isAuthenticated    = savedAuth.isAuthenticated || false;
        state.role               = savedAuth.role            || null;
        state.roleId             = savedAuth.roleId          || null;
        state.permissions        = savedAuth.permissions     || [];
        state.modules            = savedAuth.modules         || [];
        state.group              = savedAuth.group           || null;
        state.subscription       = savedAuth.subscription    || null;
        state.isTrial            = savedAuth.isTrial         || false;
        // ✅ NEW: restore company fields
        state.companyId          = savedAuth.companyId       || null;
        state.allowedStoreIds    = savedAuth.allowedStoreIds || [];
        state.selectedCompanyIds = savedAuth.selectedCompanyIds || [];
        state.twoFactorEnabled   = savedAuth.twoFactorEnabled || false;
        state.side               = savedAuth.side || null;
        const stored = savedAuth.sellviaNextRefreshAt;
        state.sellviaNextRefreshAt = (stored && stored > Date.now()) ? stored : Date.now() + 30 * 60 * 1000;
      }
    },

    setSellviaNextRefreshAt: (state, action) => {
      state.sellviaNextRefreshAt = action.payload;
      saveAuthToSession(state);
    },

    // ✅ NEW: Super-admin company selection
    // Super-admin uses this to filter which companies' data to view
    // selectedCompanyIds = [] means "show all companies"
    // selectedCompanyIds = ["id1","id2"] means "show only these 2 companies"
    setSelectedCompanyIds: (state, action) => {
      state.selectedCompanyIds = action.payload || [];
      saveAuthToSession(state);
      console.log('[authSlice] selectedCompanyIds updated:', state.selectedCompanyIds.length);
    },

    // ✅ NEW: flips right after the Settings page turns Two-Step Verification
    // on/off, so main-nav.jsx's hourly renewal reflects it immediately —
    // no re-login needed to pick up the change.
    setTwoFactorEnabled: (state, action) => {
      state.twoFactorEnabled = !!action.payload;
      saveAuthToSession(state);
    },
  },
});

export const {
  login,
  logout,
  setUser,
  updateTrialINFO,
  setTokenExpiryTimer,
  updateNavigationState,
  updateAuthData,
  setCredentials,
  restoreAuth,
  setSellviaNextRefreshAt,
  setSelectedCompanyIds,    // ✅ NEW export
  setTwoFactorEnabled,      // ✅ NEW export
} = authSlice.actions;

export const setupTokenExpiration = (expiryInSeconds) => (dispatch) => {
  const timer = setTimeout(() => {
    // Save current page before logout so login can redirect back here —
    // same convention used by the other logout paths in the app.
    const currentPath = window.location.pathname + window.location.search;
    const isAdminArea = currentPath.startsWith("/admin");
    sessionStorage.setItem(isAdminArea ? "lastAdminPage" : "lastUserPage", currentPath);

    dispatch(logout());
    // The admin login route is "/admin" itself — there is no "/admin/login" route.
    window.location.href = `${savedLoginPath(isAdminArea)}?expired=true`;
  }, expiryInSeconds * 1000);
  dispatch(setTokenExpiryTimer({ timer }));
};

export default authSlice.reducer;